import { Injectable } from '@angular/core';
import { ISeaport } from '../models';
import { SeaportPickerService } from './seaport-picker.service';
import { tap, withLatestFrom } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';

export interface SeaportPickerState {
  seaports: ISeaport[]; 
  filter: string;       
  selectedId?: number;  
  loading: boolean;
  error?: string;
}

@Injectable()
export class SeaportPickerEntityStore extends ComponentStore<SeaportPickerState>{
    constructor(private api: SeaportPickerService) {
        super({
            seaports: [],
            loading: false,
            error: undefined,
            filter: '',
            selectedId: undefined,
        });
    }

    readonly setSeaports   = this.updater((s, seaports: ISeaport[]) => ({ ...s, seaports: seaports }));
    readonly setFilter     = this.updater((s, filter: string)       => ({ ...s, filter }));
    readonly setSelectedId = this.updater((s, id?: number)          => ({ ...s, selectedId: id }));
    readonly setLoading    = this.updater((s, loading: boolean)     => ({ ...s, loading }));
    readonly setError      = this.updater((s, error?: string)       => ({ ...s, error }));

    readonly seaports$   = this.select(s => s.seaports);
    readonly filter$     = this.select(s => s.filter);
    readonly selectedId$ = this.select(s => s.selectedId);
    readonly loading$    = this.select(s => s.loading);
    readonly error$      = this.select(s => s.error);

    private readonly MIN_CHARS = 2;
    
    readonly visibleSeaports$ = this.select(
        this.seaports$, this.filter$, this.selectedId$,
        (all, filter) => {
            const searchText = (filter ?? '').trim().toLowerCase();

            // If input is less than 2 characters: show nothing
            if (searchText.length < this.MIN_CHARS) {
                return [];
            }
          
            // Otherwise, show a filtered subset
            const filteredList = all
                .filter(sp =>
                    sp.name?.toLowerCase().includes(searchText) ||
                    sp.shortCode?.toLowerCase().includes(searchText)
                )
            return filteredList;
        }
    );

    readonly syncTextFromSelection = this.effect<void>(trigger$ =>
        trigger$.pipe(
            withLatestFrom(this.selectedId$, this.seaports$, this.filter$),
            tap(([_, id, seaports, filter]) => {
                if (!filter?.trim() && id != null) {
                    const found = seaports.find(sp => sp.id === id);
                    this.setFilter(found?.name ?? '');
                }
            })
        )
    );
}